<template>
  <div class="download-reports py-3">
    <header class="dl-toolbar">
      <div class="dl-toolbar__title">
        <span class="dl-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Download Reports</h1>
          <p>One date range, eleven CSV downloads — pick a tile and go</p>
        </div>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="dl-alert">{{ errorMessage }}</CAlert>

    <section class="dl-section">
      <header class="dl-section__head">
        <h2>Date Range</h2>
        <small class="dl-section__hint">{{ rangeLabel }}</small>
      </header>
      <div class="dl-section__body">
        <div class="dl-filters">
          <div class="dl-field dl-field--range">
            <label class="dl-label">Period</label>
            <DateRangePicker v-model="dateRange" variant="form" :presets="false" />
          </div>
          <div class="dl-field dl-field--presets">
            <label class="dl-label" aria-hidden="true">&nbsp;</label>
            <div class="dl-presets" role="group" aria-label="Quick date ranges">
              <button
                v-for="preset in presets"
                :key="preset.key"
                type="button"
                class="dl-preset"
                :class="{ 'is-active': activePreset === preset.key }"
                @click="applyPreset(preset.key)"
              >
                {{ preset.label }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="dl-section">
      <header class="dl-section__head">
        <h2>Reports</h2>
        <small class="dl-section__hint">Each tile downloads a CSV for the selected date range</small>
      </header>
      <div class="dl-section__body">
        <div class="dl-grid">
          <article v-for="item in items" :key="item.id" class="dl-tile">
            <div class="dl-tile__icon" :class="`dl-tile__icon--${item.tone}`" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path :d="item.iconPath" />
                <path v-if="item.iconPath2" :d="item.iconPath2" />
              </svg>
            </div>
            <div class="dl-tile__body">
              <div class="dl-tile__title">{{ item.title }}</div>
              <div class="dl-tile__hint">{{ item.hint }}</div>
            </div>
            <div v-if="item.needsPaymentMethod" class="dl-tile__extra">
              <AppDropdown
                :value="paymentMethod"
                :options="paymentMethodOptions"
                placeholder="Select payment method"
                @input="paymentMethod = $event"
              />
            </div>
            <button
              type="button"
              class="dl-download-btn"
              :class="{ 'is-loading': loadingMap[item.id] }"
              :disabled="loadingMap[item.id]"
              @click="download(item)"
            >
              <svg v-if="!loadingMap[item.id]" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span>{{ loadingMap[item.id] ? 'Downloading…' : 'Download' }}</span>
            </button>
          </article>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import DateRangePicker from '@/components/DateRangePicker.vue'

export default {
  name: 'DownloadReports',
  components: { AppDropdown, DateRangePicker },
  data () {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return {
      fromDate: this.toIsoStatic(today),
      toDate: this.toIsoStatic(today),
      paymentMethod: 'all',
      errorMessage: '',
      loadingMap: {},
      presets: [
        { key: 'today', label: 'Today' },
        { key: '7d', label: 'Last 7 Days' },
        { key: '30d', label: 'Last 30 Days' },
        { key: 'mtd', label: 'This Month' },
        { key: 'ytd', label: 'This Year' }
      ],
      paymentMethodOptions: [
        { value: 'all', label: 'All Payment Methods' },
        { value: 'cash', label: 'Cash' },
        { value: 'cod', label: 'Cash on Delivery' },
        { value: 'vpp', label: 'VPP' },
        { value: 'google_pay', label: 'Google Pay' },
        { value: 'website_razorpay', label: 'Website Razorpay' },
        { value: 'razorpay_pages', label: 'Razorpay Pages' },
        { value: 'hdfc_bank_areekode', label: 'HDFC Bank (Areekode)' },
        { value: 'mr_federal_areekode', label: 'MR Federal Bank (Areekode)' },
        { value: 'map_federal_areekode', label: 'MAP Federal Bank (Areekode)' },
        { value: 'hdfc_bank_team', label: 'HDFC Bank (Team)' },
        { value: 'federal_bank_team', label: 'Federal Bank (Team)' },
        { value: 'google_pay_team', label: 'Google Pay (Team)' }
      ],
      items: [
        {
          id: 'payment_wise',
          title: 'Payment Method Wise',
          hint: 'Orders filtered by chosen payment method',
          api: '/api/payment-wise-resport',
          filename: 'payment_wise_report',
          tone: 'violet',
          needsPaymentMethod: true,
          payloadKeys: 'start_end',
          iconPath: 'M2 7h20v10H2z',
          iconPath2: 'M2 11h20M6 15h2'
        },
        {
          id: 'item_wise',
          title: 'Item Wise',
          hint: 'Quantity and revenue grouped by item',
          api: '/api/itemwise-report',
          filename: 'item_wise_report',
          tone: 'blue',
          payloadKeys: 'start_end',
          iconPath: 'M20 7H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z',
          iconPath2: 'M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2M2 13h20'
        },
        {
          id: 'item_wise_return',
          title: 'Item Wise Return',
          hint: 'Returned items with quantity and reason',
          api: '/api/itemwise-return-report',
          filename: 'item_wise_return_report',
          tone: 'rose',
          payloadKeys: 'start_end',
          iconPath: 'M3 12a9 9 0 1 0 9-9',
          iconPath2: 'M3 4v5h5'
        },
        {
          id: 'calls',
          title: 'Calls Report',
          hint: 'Every logged call in the period',
          api: '/api/calls-report',
          filename: 'calls_report',
          tone: 'teal',
          payloadKeys: 'start_end',
          iconPath: 'M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z'
        },
        {
          id: 'sales',
          title: 'Sales Report',
          hint: 'All sales in the selected period',
          api: '/api/sales-report',
          filename: 'sales_report',
          tone: 'green',
          payloadKeys: 'start_end',
          iconPath: 'M3 3v18h18',
          iconPath2: 'M7 14l4-4 4 4 5-6'
        },
        {
          id: 'sales_return',
          title: 'Sales Return Report',
          hint: 'Returned orders with line-item detail',
          api: '/api/return-report',
          filename: 'sales_return_report',
          tone: 'amber',
          payloadKeys: 'start_end',
          iconPath: 'M9 14l-4-4 4-4',
          iconPath2: 'M5 10h11a4 4 0 0 1 4 4v6'
        },
        // {
        //   id: 'field_visit',
        //   title: 'Field Visit Report',
        //   hint: 'Field visits logged by agents',
        //   api: '/api/field-visit-report',
        //   filename: 'field_visit_report',
        //   tone: 'emerald',
        //   payloadKeys: 'start_end',
        //   iconPath: 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z',
        //   iconPath2: 'M12 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'
        // },
        // {
        //   id: 'tickets',
        //   title: 'Tickets',
        //   hint: 'Support tickets raised in the period',
        //   api: '/api/tickets/export_tickets',
        //   filename: 'tickets_report',
        //   tone: 'slate',
        //   payloadKeys: 'start_end',
        //   iconPath: 'M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-4z',
        //   iconPath2: 'M13 5v2M13 11v2M13 17v2'
        // },
        // {
        //   id: 'priority',
        //   title: 'Priority Call List',
        //   hint: 'Calls queued at priority for follow-up',
        //   api: '/api/calls_queue/export_priority_list',
        //   filename: 'priority_call_list',
        //   tone: 'rose',
        //   payloadKeys: 'start_end',
        //   iconPath: 'M12 2l2.39 4.84L19.8 7.7l-3.9 3.8.92 5.36L12 14.27 7.18 16.86l.92-5.36-3.9-3.8 5.41-.86z'
        // },
        {
          id: 'call_summary',
          title: 'Call Summary',
          hint: 'Aggregate call metrics by agent',
          api: '/api/calls/export_summary',
          filename: 'call_summary',
          tone: 'blue',
          payloadKeys: 'start_end',
          iconPath: 'M9 11l3 3 8-8',
          iconPath2: 'M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11'
        },
        {
          id: 'followup_grades',
          title: 'Followup Grades',
          hint: 'Graded followups across the period',
          api: '/api/exports/mdi_graded_followups',
          filename: 'followup_grades',
          tone: 'violet',
          payloadKeys: 'start_end',
          iconPath: 'M22 11.08V12a10 10 0 1 1-5.93-9.14',
          iconPath2: 'M22 4L12 14.01l-3-3'
        }
      ]
    }
  },
  computed: {
    dateRange: {
      get () {
        return { fromDate: this.fromDate, toDate: this.toDate }
      },
      set (value) {
        this.fromDate = (value && value.fromDate) || ''
        this.toDate = (value && value.toDate) || ''
      }
    },
    rangeLabel () {
      if (!this.fromDate || !this.toDate) return ''
      if (this.fromDate === this.toDate) return this.formatDisplay(this.fromDate)
      return `${this.formatDisplay(this.fromDate)} → ${this.formatDisplay(this.toDate)}`
    },
    activePreset () {
      if (!this.fromDate || !this.toDate) return ''
      for (const preset of this.presets) {
        const range = this.rangeForPreset(preset.key)
        if (range && range.fromDate === this.fromDate && range.toDate === this.toDate) {
          return preset.key
        }
      }
      return ''
    }
  },
  methods: {
    authToken () {
      return localStorage.getItem('api_token')
    },
    toIsoStatic (date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    toIso (date) {
      return this.toIsoStatic(date)
    },
    formatDisplay (iso) {
      if (!iso) return ''
      const [y, m, d] = iso.split('-')
      if (!y || !m || !d) return iso
      return `${d}-${m}-${y}`
    },
    rangeForPreset (key) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const start = new Date(today)
      const end = new Date(today)
      switch (key) {
        case 'today':
          break
        case '7d':
          start.setDate(today.getDate() - 6)
          break
        case '30d':
          start.setDate(today.getDate() - 29)
          break
        case 'mtd':
          start.setDate(1)
          break
        case 'ytd':
          start.setMonth(0, 1)
          break
        default:
          return null
      }
      return { fromDate: this.toIso(start), toDate: this.toIso(end) }
    },
    applyPreset (key) {
      const range = this.rangeForPreset(key)
      if (!range) return
      this.fromDate = range.fromDate
      this.toDate = range.toDate
    },
    buildPayload (item) {
      const payload = { start_date: this.fromDate, end_date: this.toDate }
      if (item.needsPaymentMethod) {
        payload.payment_method = this.paymentMethod || 'all'
      }
      return payload
    },
    download (item) {
      this.errorMessage = ''
      if (!this.fromDate || !this.toDate) {
        this.errorMessage = 'Please pick a date range first.'
        return
      }
      if (item.needsPaymentMethod && !this.paymentMethod) {
        this.errorMessage = 'Please pick a payment method for the Payment Method Wise report.'
        return
      }

      this.$set(this.loadingMap, item.id, true)
      axios({
        url: `${this.$apiAdress}${item.api}?token=${this.authToken()}`,
        method: 'POST',
        data: this.buildPayload(item),
        responseType: 'blob'
      })
        .then((response) => {
          const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
          const anchor = document.createElement('a')
          anchor.href = blobUrl
          anchor.setAttribute('download', `${item.filename}_${this.fromDate}_${this.toDate}.csv`)
          document.body.appendChild(anchor)
          anchor.click()
          anchor.remove()
          window.URL.revokeObjectURL(blobUrl)
        })
        .catch(async (error) => {
          console.log(error)
          const status = error && error.response && error.response.status
          let detail = ''
          const blob = error && error.response && error.response.data
          if (blob && typeof blob.text === 'function') {
            try {
              const text = await blob.text()
              try {
                const parsed = JSON.parse(text)
                detail = parsed.message || parsed.error || parsed.exception || text
              } catch (e) {
                detail = text
              }
            } catch (e) {
              detail = ''
            }
          } else if (error && error.message) {
            detail = error.message
          }
          if (detail && detail.length > 300) detail = detail.slice(0, 300) + '…'
          const prefix = status ? `Unable to download ${item.title} (HTTP ${status})` : `Unable to download ${item.title}.`
          this.errorMessage = detail ? `${prefix}: ${detail}` : prefix
        })
        .finally(() => {
          this.$set(this.loadingMap, item.id, false)
        })
    }
  }
}
</script>

<style scoped>
.download-reports {
  padding: 0.25rem 0;
}

.dl-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.dl-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.dl-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.dl-toolbar__title p {
  margin: 0.1rem 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.dl-toolbar__accent {
  width: 4px;
  height: 32px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
}

.dl-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.dl-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.dl-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.dl-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.dl-section__hint {
  color: #6b7280;
  font-size: 0.78rem;
}

.dl-section__body {
  padding: 1rem;
}

.dl-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.dl-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.dl-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.dl-field--range {
  flex: 0 1 460px;
  max-width: 480px;
}

.dl-field--presets {
  flex: 1 1 320px;
  min-width: 0;
}

.dl-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  min-height: 36px;
  align-items: center;
}

.dl-preset {
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: 0 0.85rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.dl-preset:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.dl-preset:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.dl-preset.is-active {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
}

.dl-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 0.85rem;
}

.dl-tile {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  padding: 1rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.dl-tile:hover {
  border-color: #c8e6d8;
  box-shadow: 0 6px 16px rgba(14, 91, 70, 0.08);
  transform: translateY(-1px);
}

.dl-tile__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
}

.dl-tile__icon--green   { background: #dff5ea; color: #0e5b46; }
.dl-tile__icon--amber   { background: #fef3c7; color: #92400e; }
.dl-tile__icon--blue    { background: #e0edff; color: #1e40af; }
.dl-tile__icon--violet  { background: #ede9fe; color: #5b21b6; }
.dl-tile__icon--teal    { background: #ccfbf1; color: #0f766e; }
.dl-tile__icon--rose    { background: #ffe4e6; color: #9f1239; }
.dl-tile__icon--slate   { background: #e2e8f0; color: #334155; }
.dl-tile__icon--emerald { background: #d1fae5; color: #065f46; }

.dl-tile__body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.dl-tile__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.dl-tile__hint {
  font-size: 0.78rem;
  color: #6b7280;
}

.dl-tile__extra {
  margin-top: 0.1rem;
}

.dl-download-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  height: 36px;
  padding: 0 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
  cursor: pointer;
  align-self: flex-start;
}

.dl-download-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.dl-download-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.28);
}

.dl-download-btn:disabled,
.dl-download-btn.is-loading {
  opacity: 0.85;
  cursor: progress;
}
</style>
